import React, { Suspense, lazy } from 'react';
import { Route, Routes, useLocation, Navigate } from 'react-router-dom';
import Layout from "./Layout.jsx";
import LoadingScreen from "../components/LoadingScreen";
import ProtectRoute from "../components/ProtectRoute";
import { useAuth } from "@/contexts/AuthContext";
const Dashboard = lazy(() => import("./Dashboard"));
const Clients = lazy(() => import("./Clients"));
const Pipeline = lazy(() => import("./Pipeline"));
const Projects = lazy(() => import("./Projects"));
const MapExplorer = lazy(() => import("./MapExplorer"));
const ClientDetail = lazy(() => import("./ClientDetail"));
const Settings = lazy(() => import("./Settings"));
const Calendar = lazy(() => import("./Calendar"));
const WorkGroups = lazy(() => import("./WorkGroups"));
const Tasks = lazy(() => import("./Tasks"));
const WorkGroupDashboard = lazy(() => import("./WorkGroupDashboard"));
const UserManagement = lazy(() => import("./UserManagement"));
const TeamManagement = lazy(() => import("./TeamManagement"));
const Landing = lazy(() => import("./Landing"));
// Deleted: JoinTeam and InvitePage removed (Phase 1 - replaced by direct worker creation)
const SalesDashboard = lazy(() => import("./SalesDashboard"));
const ColdCalls = lazy(() => import("./ColdCalls"));
const Finance = lazy(() => import("./Finance"));
const FounderEarnings = lazy(() => import("./FounderEarnings"));
const DiscoverCRM = lazy(() => import("./DiscoverCRM"));
const Admin = lazy(() => import("./Admin"));
const MonthlyReport = lazy(() => import("./MonthlyReport"));
const InvoiceRequest = lazy(() => import("./InvoiceRequest"));
const OnboardingWizard = lazy(() => import("./OnboardingWizard"));
const NotFound = lazy(() => import("./NotFound"));

// Auth Pages
const Login = lazy(() => import("./Login"));
const Register = lazy(() => import("./Register"));
const ForgotPassword = lazy(() => import("./ForgotPassword"));
const ResetPassword = lazy(() => import("./ResetPassword"));
const ChangePassword = lazy(() => import("./ChangePassword"));
const AcceptInvite = lazy(() => import("./AcceptInvite"));

// Legal Pages
const PrivacyPolicy = lazy(() => import("./legal/PrivacyInfo"));
const CookiePolicy = lazy(() => import("./legal/CookiesInfo"));
const TermsOfService = lazy(() => import("./legal/TermsOfService"));
const GDPRCompliance = lazy(() => import("./legal/GDPR"));
const DataManagement = lazy(() => import("./legal/DataManagement"));
const LegalDocuments = lazy(() => import("./legal/LegalDocuments"));

const PAGES = {
    Dashboard: Dashboard,
    Clients: Clients,
    Pipeline: Pipeline,
    Projects: Projects,
    MapExplorer: MapExplorer,
    ClientDetail: ClientDetail,
    Settings: Settings,
    Calendar: Calendar,
    WorkGroups: WorkGroups,
    Tasks: Tasks,
    WorkGroupDashboard: WorkGroupDashboard,
    UserManagement: UserManagement,
    TeamManagement: TeamManagement,
    Landing: Landing,
    SalesDashboard: SalesDashboard,
    ColdCalls: ColdCalls,
    Finance: Finance,
    FounderEarnings: FounderEarnings,
    DiscoverCRM: DiscoverCRM,
    Admin: Admin,
    MonthlyReport: MonthlyReport,
    InvoiceRequest: InvoiceRequest,
}

const _getCurrentPage = (url) => {
    if (url === '/' || url === '') return 'Landing';

    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }
    let urlLastPart = url.split('/').pop();
    if (urlLastPart.includes('?')) {
        urlLastPart = urlLastPart.split('?')[0];
    }

    if (urlLastPart === 'discover-crm') return 'DiscoverCRM';

    const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === urlLastPart.toLowerCase());
    return pageName || 'Dashboard';
}

// Bounce already-authenticated users away from the auth pages
function PublicOnly({ children }) {
    const { isAuthenticated, isLoading, mustChangePassword } = useAuth();
    if (isLoading) return <LoadingScreen />;
    if (isAuthenticated && mustChangePassword) return <Navigate to="/change-password" replace />;
    if (isAuthenticated) return <Navigate to="/Dashboard" replace />;
    return children;
}

// Create a wrapper component that uses useLocation inside the Router context
function PagesContent() {
    const location = useLocation();
    const currentPage = _getCurrentPage(location.pathname);

    return (
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-dark-bg"><div className="animate-spin w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full" /></div>}>
            <Routes>
                {/* ── Auth Routes (standalone, no CRM layout) ── */}
                <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
                <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
                <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />

                {/* ── Legal Routes (standalone, no CRM layout) ── */}
                <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
                <Route path="/legal/terms-of-service" element={<TermsOfService />} />
                <Route path="/legal/gdpr" element={<GDPRCompliance />} />
                <Route path="/legal/data-management" element={<DataManagement />} />
                <Route path="/legal/documents" element={<LegalDocuments />} />

                {/* ── Onboarding Wizard (standalone, no CRM layout) ── */}
                <Route path="/OnboardingWizard" element={<OnboardingWizard />} />

                {/* ── All other routes wrapped in CRM Layout ── */}
                <Route path="/*" element={
                    <Layout currentPageName={currentPage}>
                        <Routes>
                            {/* Public Routes */}
                            <Route path="/" element={<Landing />} />
                            <Route path="/Landing" element={<Navigate to="/" replace />} />
                            <Route path="/discover-crm" element={<DiscoverCRM />} />

                            {/* Protected Routes */}
                            <Route path="/Dashboard" element={<ProtectRoute pageName="Dashboard"><Dashboard /></ProtectRoute>} />
                            <Route path="/Clients" element={<ProtectRoute pageName="Clients"><Clients /></ProtectRoute>} />
                            <Route path="/Pipeline" element={<ProtectRoute pageName="Pipeline"><Pipeline /></ProtectRoute>} />
                            <Route path="/Projects" element={<ProtectRoute pageName="Projects"><Projects /></ProtectRoute>} />
                            <Route path="/MapExplorer" element={<ProtectRoute pageName="MapExplorer"><MapExplorer /></ProtectRoute>} />
                            <Route path="/ClientDetail" element={<ProtectRoute pageName="ClientDetail"><ClientDetail /></ProtectRoute>} />
                            <Route path="/Settings" element={<ProtectRoute pageName="Settings"><Settings /></ProtectRoute>} />
                            <Route path="/Calendar" element={<ProtectRoute pageName="Calendar"><Calendar /></ProtectRoute>} />
                            <Route path="/WorkGroups" element={<ProtectRoute pageName="WorkGroups"><WorkGroups /></ProtectRoute>} />
                            <Route path="/Tasks" element={<ProtectRoute pageName="Tasks"><Tasks /></ProtectRoute>} />
                            <Route path="/WorkGroupDashboard" element={<ProtectRoute pageName="WorkGroupDashboard"><WorkGroupDashboard /></ProtectRoute>} />
                            <Route path="/UserManagement" element={<ProtectRoute pageName="UserManagement"><UserManagement /></ProtectRoute>} />
                            <Route path="/TeamManagement" element={<ProtectRoute pageName="TeamManagement"><TeamManagement /></ProtectRoute>} />
                            <Route path="/SalesDashboard" element={<ProtectRoute pageName="SalesDashboard"><SalesDashboard /></ProtectRoute>} />
                            <Route path="/ColdCalls" element={<ProtectRoute pageName="ColdCalls"><ColdCalls /></ProtectRoute>} />
                            <Route path="/Finance" element={<ProtectRoute pageName="Finance"><Finance /></ProtectRoute>} />
                            <Route path="/FounderEarnings" element={<ProtectRoute pageName="FounderEarnings"><FounderEarnings /></ProtectRoute>} />
                            <Route path="/MonthlyReport" element={<ProtectRoute pageName="MonthlyReport"><MonthlyReport /></ProtectRoute>} />
                            <Route path="/InvoiceRequest" element={<ProtectRoute pageName="InvoiceRequest"><InvoiceRequest /></ProtectRoute>} />
                            <Route path="/Admin" element={<ProtectRoute pageName="Admin"><Admin /></ProtectRoute>} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Layout>
                } />
            </Routes>
        </Suspense>
    );
}

export default function Pages() {
    return (
        <PagesContent />
    );
}
